import {useNavigate, useOutletContext, useParams} from 'react-router-dom';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import withPaginationV2 from '../../../hoc/pagination/withPaginationV2';
import AdmicityTableV2 from '../../../shared-components/Table/V2/AdmicityTableV2';
import {
    useGetEmployeeNotificationFormsQuery,
    useGetEmployeeLinkedSelfServiceFormsQuery,
    useGetFormsByEmployeeQuery,
    useGetCommunicationFormsByEmployeeQuery,
    useLazyGetEmployeeNotificationFormResponsesQuery,
    useClaimFormRequestMutation,
    useUnclaimFormRequestMutation,
    useGetMyFormsQuery,
    useGetMyCommunicationFormsQuery,
    useGetMyNotificationFormsQuery,
    useGetMyLinkedSelfServiceFormsQuery,
    useLazyGetMyNotificationFormResponsesQuery
} from '../../../api/services/formRequestsService';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import ContentPasteOffRoundedIcon from '@mui/icons-material/ContentPasteOffRounded';
import {FORM_REQUEST_STATUSES, FORM_REQUEST_STATUSES_NAMES} from '../../../constants/formRequestStatuses';
import FILTER_OPERATIONS from '../../../constants/filterOperations';
import AdmicityStatus from '../../../shared-components/AdmicityStatus';
import {getFormRequestStatusInfo} from '../RequestAndResponses/common/common';
import {formatDate} from '../../../utility/dateUtil';
import PropTypes from 'prop-types';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
    downloadFormResponsePdfReport,
    useLazyGenerateFormResponsesReportByEmployeeQuery,
    useLazyGenerateFormResponsesReportByStudentQuery
} from '../../../api/services/filesService';
import useFileDownloader from '../../../utility/hooks/useFileDownloader';
import useNotification from '../../../utility/hooks/useNotification';
import AdmicityTabs from '../../../shared-components/AdmicityTabs';
import Paper from '@mui/material/Paper';
import {useTheme} from '@mui/material/styles';
import SelfServiceForms from '../Profiles/ProfileTabs/FormsTab/SelfServiceForms';
import {Alert, Box, Typography} from '@mui/material';
import UnderTab from '../Profiles/ProfileTabs/FormsTab/UnderTab';
import {TAB_OUTLINE} from '../Profiles/ProfileTabs/FormsTab/tabOutline';
import {COMPACT_TABLE_PROPS, communicationRowButtons, formRowButtons, openCommunicationRow, openFormRow, SUB_TAB_SX, withHyphenWhenEmpty, withToolbarAboveOnSmallScreen} from '../Profiles/ProfileTabs/FormsTab/formsTableStyle';
import ApprovalFormsTable from '../../../shared-components/ApprovalFormsTable';
import {getApprovalStatusInfo} from '../../../constants/FormApprovalStatuses';
import useUser from '../../../utility/hooks/useUser';
import {useGetEmployeeByCurrentUserQuery, useGetEmployeeByIdQuery} from '../../../api/services/employeeService';

/* eslint-disable react/prop-types */
const allLinkedEmployeesCanSubmit = (row) => {
    const rowData = row?.original ?? row;
    return !!(rowData?.form?.allLinkedEmployeesCanSubmit ?? rowData?.form?.AllLinkedEmployeesCanSubmit);
};

const preSubmittedColumns = [
    {
        accessorKey: 'form.name',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS,
    },
    {
        id: 'firstName',
        header: 'First Name',
        accessorFn: (row) => row?.student?.forename || row?.employee?.forename || '',
        filterFn: FILTER_OPERATIONS.CONTAINS,
    },
    {
        id: 'lastName',
        header: 'Last Name',
        accessorFn: (row) => row?.student?.legalSurname || row?.employee?.legalSurname || '',
        filterFn: FILTER_OPERATIONS.CONTAINS,
    },
    {
        accessorKey: 'status',
        header: 'Status',
        // Maria, 18 October 2026: a form not yet emailed ('Created') shows as 'Awaiting submission'
        Cell: ({cell}) => <AdmicityStatus {...getFormRequestStatusInfo(cell.getValue() === FORM_REQUEST_STATUSES.created ? FORM_REQUEST_STATUSES.sent : cell.getValue())} />,
        filterVariant: 'select',
        filterFn: FILTER_OPERATIONS.EQUALS,
        filterSelectOptions: Object.values(FORM_REQUEST_STATUSES)
            .filter(statusId => statusId !== FORM_REQUEST_STATUSES.created)
            .map(statusId => ({
                value: statusId,
                label: FORM_REQUEST_STATUSES_NAMES[statusId]
            })),
    },
    {
        accessorKey: 'expirationDate',
        // Maria, 18 October 2026: 'Expiry', without the information pop-up
        header: 'Expiry',
        Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
        enableColumnFilter: false
    },
];

const claimColumns = [
    {
        accessorKey: 'claimedBy',
        header: 'Claimed By',
        enableColumnFilter: false,
        Cell: ({
                   row,
                   cell
               }) => allLinkedEmployeesCanSubmit(row) ? '' : cell.getValue(),
    },
    {
        accessorKey: 'claimedAt',
        header: 'Claimed At',
        Cell: ({
                   row,
                   cell
               }) =>
            allLinkedEmployeesCanSubmit(row)
                ? ''
                : (cell.getValue() ? formatDate(cell.getValue()) : ''),
        enableColumnFilter: false,
    },
];

const submittedAndApprovalColumns = [
    {
        id: 'editedBy',
        accessorFn: (row) => row?.editedBy?.fullname ?? '',
        header: 'Submitted By',
        enableColumnFilter: false
    },
    {
        accessorKey: 'editedAt',
        header: 'Submitted At',
        Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalStage',
        header: 'Approval Stage',
        Cell: ({row}) => row.original?.approvalStage ?? '',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalStageProgress',
        header: 'Approval Progress',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalStatus',
        header: 'Approval Status',
        Cell: ({cell: {getValue}}) => (
            <AdmicityStatus {...getApprovalStatusInfo(getValue())} />
        ),
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvedBy',
        header: 'Approved By',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvedAt',
        header: 'Approved At',
        Cell: ({cell}) => cell.getValue() ? formatDate(cell.getValue()) : '',
        enableColumnFilter: false,
    },
    {
        accessorKey: 'approvalNote',
        header: 'Approval Note',
        enableColumnFilter: false,
    },
];

// Maria, 19 October 2026: on Student Forms the student's name is in grey under the form name, to save space
const formWithPersonColumn = {
    accessorKey: 'form.name',
    header: 'Form Name',
    filterFn: FILTER_OPERATIONS.CONTAINS,
    Cell: ({row}) => (
        <Box>
            <Typography variant="body2" sx={{lineHeight: 1.3}}>{row.original?.form?.name}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{lineHeight: 1.3}}>
                {[row.original?.student?.forename || row.original?.employee?.forename,
                    row.original?.student?.legalSurname || row.original?.employee?.legalSurname].filter(Boolean).join(' ')}
            </Typography>
        </Box>
    )
};

// Maria, 19 October 2026: a lighter line round the open sub-tab and its table
// Maria, 19 October 2026: each list is as tall as its rows; inside the box under the buttons row a table that shares
// out the height of its box shrank to nothing on screens taller than 600px
const STAFF_TABLE_PROPS = {
    ...COMPACT_TABLE_PROPS,
    muiTableContainerProps: {...COMPACT_TABLE_PROPS.muiTableContainerProps, sx: {flex: '0 0 auto'}}
};
const STAFF_TAB_OUTLINE = '#9fd4c7';
// Maria, 19 October 2026: the sub-tabs look like the student Forms tab's; the sub-categories under Forms and Self-service
// Forms (the member of staff's own, Linked Staff and Linked Student) are tabs in the same style, a little smaller
const STAFF_SUB_TAB_SX = SUB_TAB_SX;
// Maria, 19 October 2026: the second row looks different from the first, so it is clear which is which: rounded buttons
// on a light green row, the open one filled in green
const INNER_SUB_TAB_SX = {
    minHeight: 30,
    height: 30,
    my: 0.75,
    mr: 1,
    px: 1.75,
    py: 0,
    fontSize: 13,
    textTransform: 'none',
    fontWeight: 600,
    color: '#0A4F45',
    backgroundColor: '#fff',
    border: '1px solid #9fd4c7',
    borderRadius: '15px',
    '&.Mui-selected': {color: '#fff !important', backgroundColor: '#0A8572', borderColor: '#0A8572'}
};
const FIRST_INNER_SUB_TAB_SX = INNER_SUB_TAB_SX;
const HOLDS_BUTTONS_SUB_TAB_SX = SUB_TAB_SX;

// and 8px after the last one

// Maria, 19 October 2026: no 'Submitted By' on Forms, as this member of staff submits every one of them
// Maria, 19 October 2026: the member of staff's own forms have no name columns, as on Student Forms; the name is in the
// heading of the page
const getColumns = (includeClaimColumns = false, nameUnderForm = false, withSubmittedBy = true, ownForms = false) => withHyphenWhenEmpty([
    ...(nameUnderForm
        ? [formWithPersonColumn, ...preSubmittedColumns.filter(column => !['form.name', 'firstName', 'lastName'].includes(column.accessorKey ?? column.id))]
        : ownForms
            ? preSubmittedColumns.filter(column => !['firstName', 'lastName'].includes(column.id))
            : preSubmittedColumns),
    ...(includeClaimColumns ? claimColumns : []),
    ...submittedAndApprovalColumns.filter(column => withSubmittedBy || column.id !== 'editedBy'),
]);

/* eslint-enable react/prop-types */
const communicationFormsTableColumns = [
    {
        accessorKey: 'formName',
        header: 'Form Name',
        filterFn: FILTER_OPERATIONS.CONTAINS,
    },
    {
        accessorKey: 'sentAt',
        header: 'Date',
        Cell: ({cell}) => {
            const date = cell.getValue();
            return date ? formatDate(date) : '';
        },
        enableColumnFilter: false,
    },
];

// Maria, 19 October 2026: an Employee's My Forms is this same Forms tab, with their own forms ('self'), so both places
// show the same lists in the same layout
// Maria, 20 October 2026: the My Profile wording, with the forms in place of the profile
export const NOT_LINKED_FORMS_MESSAGE = 'Your Admicity account has not yet been linked to your staff record in SIMS. ' +
    'Your forms will be shown here after the next SIMS synchronisation. ' +
    'The email address on your SIMS staff record must match the email address you use to log in to Admicity.';

const EmployeeForms = ({self = false}) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(`${theme.breakpoints.down('sm')}, (max-height:600px)`);
    // AD-2735: on a small screen the table's icons are above the table, clear of the tabs
    const staffTableProps = withToolbarAboveOnSmallScreen(STAFF_TABLE_PROPS, isSmallScreen);
    const isNarrow = useMediaQuery('(max-width:899.95px)');
    const {id: routeEmployeeId} = useParams();
    const {data: currentEmployee, error: currentEmployeeError} = useGetEmployeeByCurrentUserQuery(undefined, {skip: !self});
    const employeeId = self ? currentEmployee?.id : routeEmployeeId;
    const employeeIdNumber = Number(employeeId);
    const backPath = self ? '/request-and-responses' : `/profiles/employees/${employeeId}/forms`;
    const [generateMyFormsReport] = useLazyGenerateFormResponsesReportByStudentQuery();
    const navigate = useNavigate();
    const {user} = useUser();
    const downloadFile = useFileDownloader();
    const {
        showSuccessNotification,
        showErrorNotification
    } = useNotification();
    const [generateFormResponsesReportByEmployee] = useLazyGenerateFormResponsesReportByEmployeeQuery();
    const {
        key,
        formsState,
        setFormsState
    } = useOutletContext();
    const [currentTabIndex, setCurrentTabIndex] = useState(formsState?.currentTabIndex ?? 0);
    const [formsTabIndex, setFormsTabIndex] = useState(formsState?.formsTabIndex ?? 0);
    const [selfServiceTabIndex, setSelfServiceTabIndex] = useState(formsState?.selfServiceTabIndex ?? 0);
    const {data: employeeById} = useGetEmployeeByIdQuery(employeeId, {skip: self || !employeeId});
    const employee = self ? currentEmployee : employeeById;
    const [selectedNotificationFormIndex, setSelectedNotificationFormIndex] = useState(formsState?.selectedNotificationFormIndex);
    const [claimFormRequest, {isLoading: isClaimingFormRequest}] = useClaimFormRequestMutation();
    const [unclaimFormRequest, {isLoading: isUnclaimingFormRequest}] = useUnclaimFormRequestMutation();
    const {
        data: notificationForms = [],
        isLoading: isLoadingNotificationForms,
        isFetching: isFetchingNotificationForms,
    } = (self ? useGetMyNotificationFormsQuery : useGetEmployeeNotificationFormsQuery)(self ? undefined : employeeId, {skip: currentTabIndex !== 1 || !employeeId});
    const [selectedLinkedFormIndex, setSelectedLinkedFormIndex] = useState(undefined);
    const {
        data: linkedSelfServiceForms = [],
        isLoading: isLoadingLinkedForms,
        isFetching: isFetchingLinkedForms,
    } = (self ? useGetMyLinkedSelfServiceFormsQuery : useGetEmployeeLinkedSelfServiceFormsQuery)(self ? undefined : employeeId, {skip: currentTabIndex !== 1 || !employeeId});
    const [selectedStaffLinkedFormIndex, setSelectedStaffLinkedFormIndex] = useState(undefined);

    const formsQuery = self ? useGetMyFormsQuery : useGetFormsByEmployeeQuery;
    const Table = useMemo(() => withPaginationV2(AdmicityTableV2, {
        func: formsQuery,
        props: {
            ...(self ? {} : {employeeId}),
            linkToStudents: false,
            aboutEmployee: true
        }
    }), [employeeId, self]);
    // Maria, 19 October 2026: forms about another member of staff that this member of staff completes, on their own tab
    const StaffTable = useMemo(() => withPaginationV2(AdmicityTableV2, {
        func: formsQuery,
        props: {
            ...(self ? {} : {employeeId}),
            linkToStudents: false,
            aboutEmployee: false
        }
    }), [employeeId, self]);
    const StudentTable = useMemo(() => withPaginationV2(AdmicityTableV2, {
        func: formsQuery,
        props: {
            ...(self ? {} : {employeeId}),
            linkToStudents: true
        }
    }), [employeeId, self]);
    const CommunicationFormsTable = useMemo(() => withPaginationV2(AdmicityTableV2, {
        func: self ? useGetMyCommunicationFormsQuery : useGetCommunicationFormsByEmployeeQuery,
        props: self ? {} : {employeeId}
    }), [employeeId, self]);

    useEffect(() => {
        return () => {
            setFormsState(prev => ({
                ...prev,
                currentTabIndex,
                formsTabIndex,
                selfServiceTabIndex,
                selectedNotificationFormIndex
            }));
        };
    }, [currentTabIndex, formsTabIndex, selfServiceTabIndex, selectedNotificationFormIndex]);

    const handleClaimFormRequest = async (formRequestId) => {
        try {
            const payload = {
                formRequestId,
                employeeId: Number(employeeId)
            };

            await claimFormRequest(payload).unwrap();
            showSuccessNotification('Form claimed');
        } catch (error) {
            showErrorNotification(error?.message || 'Failed to claim form request');
        }
    };

    const handleUnclaimFormRequest = async (formRequestId) => {
        try {
            const payload = {
                formRequestId,
                employeeId: Number(employeeId)
            };

            await unclaimFormRequest(payload).unwrap();
            showSuccessNotification('Form unclaimed');
        } catch (error) {
            showErrorNotification(error?.message || 'Failed to unclaim form request');
        }
    };

    const getRowData = (row) => row?.original ?? row;

    const downloadPdfReport = async (formRequestId = null, formId = null, isBulk = true) => {
        if (formRequestId && !isBulk) {
            await downloadFile(
                downloadFormResponsePdfReport,
                {
                    formId,
                    formRequestId
                });
        } else {
            if (self) await generateMyFormsReport({formId, allStudents: true});
            else await generateFormResponsesReportByEmployee({employeeId});
            showSuccessNotification('The PDFs is being created. It will be sent to your email address when the process is complete.');
        }
    };

    const studentRowButtons = formRowButtons({
        open: request => navigate(`${request.id}`, {
            state: {navigateBackRoute: {pathname: backPath, state: {activeTabIndex: 0}}}
        }),
        // AD-2695: only on a response this member of staff submitted, on a form that allows editing
        canEdit: request => !!request.form?.allowResponseEditing && !!user?.id && request.editedBy?.id === user.id,
        edit: request => navigate(`${request.id}?edit=1`),
        claim: {
            needsClaim: request => !allLinkedEmployeesCanSubmit(request),
            claimedByMe: request => isClaimedByCurrentEmployee(request),
            claim: request => handleClaimFormRequest(request.id),
            unclaim: request => handleUnclaimFormRequest(request.id),
            busy: isClaimingFormRequest || isUnclaimingFormRequest
        },
        // AD-2754: on a phone the buttons column fits its buttons, as on a student's Forms tab (AD-2750)
        compact: isSmallScreen
    });

    const staffRowButtons = formRowButtons({
        open: request => navigate(`${request.id}`, {
            state: {navigateBackRoute: {pathname: backPath, state: {activeTabIndex: 0}}}
        }),
        canEdit: request => !!request.form?.allowResponseEditing && !!user?.id && request.editedBy?.id === user.id,
        edit: request => navigate(`${request.id}?edit=1`),
        claim: {
            needsClaim: request => !allLinkedEmployeesCanSubmit(request),
            claimedByMe: request => isClaimedByCurrentEmployee(request),
            claim: request => handleClaimFormRequest(request.id),
            unclaim: request => handleUnclaimFormRequest(request.id),
            busy: isClaimingFormRequest || isUnclaimingFormRequest
        },
        // AD-2754: on a phone the buttons column fits its buttons, as on a student's Forms tab (AD-2750)
        compact: isSmallScreen
    });

    const isClaimedByCurrentEmployee = (row) => {
        const rowData = getRowData(row);
        return !!rowData.claimedByEmployeeId && String(rowData.claimedByEmployeeId) === String(employeeId);
    };

    // Maria, 19 October 2026: an 'Actions' heading and the icons on the row of tabs above, as on Student Forms
    const rowButtons = formRowButtons({
        open: request => navigate(`${request.id}`),
        // AD-2695: only on a response this member of staff submitted, on a form that allows editing
        canEdit: request => !!request.form?.allowResponseEditing && !!user?.id && request.editedBy?.id === user.id,
        edit: request => navigate(`${request.id}?edit=1`),
        compact: isSmallScreen
    });

    // Maria, 19 October 2026: Forms and Self-service Forms each have their own row of tabs: this member of staff's own
    // forms, forms linked to another member of staff, and forms linked to a student
    // Maria, 19 October 2026: without the word 'Forms', so the three buttons fit under their tab
    const ownFormsLabel = `${employee?.legalForename || 'Own'}'s`;
    const formsTabs = [
        {
                label: ownFormsLabel,
                testId: 'tab-own-forms',
                tabSx: FIRST_INNER_SUB_TAB_SX,
                component: <>
                <Table
                    id={key}
                    variant="elevation"
                    columns={getColumns(false, false, false, true)}
                    tableProps={{
                        ...staffTableProps,
                        // the buttons are in the actions column, which stays in view at the right
                        enableRowActions: true,
                        displayColumnDefOptions: {'mrt-row-actions': rowButtons},
                        enableSorting: false,
                        enableFullScreenToggle: false
                    }}
                    onRowClick={(params) => openFormRow(params, request => navigate(`${request.id}`), isSmallScreen)}
                    toolbarIconActions={[
                        {
                            icon: <FileDownloadRoundedIcon/>,
                            title: 'Download report',
                            type: 'select',
                            hideIfEmpty: true,
                            menuItems: [
                                {
                                    label: 'Download PDF',
                                    onClick: downloadPdfReport
                                },
                            ]
                        }
                    ]}
                    noRowsOverlay={{
                        icon: <ContentPasteOffRoundedIcon/>,
                        text: 'No requests and responses yet'
                    }}
                />
                </>
            },
        {
                // Maria, 19 October 2026: forms about another member of staff, with that person's name under the form name
                label: 'Linked Staff',
                testId: 'tab-linked-staff-forms',
                tabSx: INNER_SUB_TAB_SX,
                // Maria, 19 October 2026: ends in line with the Forms tab of the staff profile
                component: <>
                    <StaffTable
                        id={`${key}/staff-forms`}
                        variant="elevation"
                        columns={getColumns(false, true)}
                        tableProps={{
                            ...staffTableProps,
                            // Maria, 19 October 2026: the buttons in the actions column, as on Forms, with 'Claim'
                            enableRowActions: true,
                            displayColumnDefOptions: {'mrt-row-actions': staffRowButtons},
                            enableSorting: false,
                            enableFullScreenToggle: false
                        }}
                        onRowClick={(params) => openFormRow(params, request => navigate(`${request.id}`, {
                            state: {
                                navigateBackRoute: {
                                    pathname: backPath,
                                    state: {activeTabIndex: 0}
                                }
                            }
                        }), isSmallScreen)}
                        toolbarIconActions={[
                            {
                                icon: <FileDownloadRoundedIcon/>,
                                title: 'Download report',
                                type: 'select',
                                hideIfEmpty: true,
                                menuItems: [
                                    {
                                        label: 'Download PDF',
                                        onClick: downloadPdfReport
                                    },
                                ]
                            }
                        ]}
                        noRowsOverlay={{
                            icon: <ContentPasteOffRoundedIcon/>,
                            text: 'No staff form requests yet'
                        }}
                    />
                </>
            },
        {
                label: 'Linked Student',
                testId: 'tab-linked-student-forms',
                tabSx: INNER_SUB_TAB_SX,
                // Maria, 19 October 2026: ends in line with the Forms tab of the staff profile
                component: <>
                    <StudentTable
                        id={`${key}/student-forms`}
                        variant="elevation"
                        columns={getColumns(false, true)}
                        tableProps={{
                            ...staffTableProps,
                            // Maria, 19 October 2026: the buttons in the actions column, as on Forms, with 'Claim'
                            enableRowActions: true,
                            displayColumnDefOptions: {'mrt-row-actions': studentRowButtons},
                            enableSorting: false,
                            enableFullScreenToggle: false
                        }}
                        onRowClick={(params) => openFormRow(params, request => navigate(`${request.id}`, {
                            state: {
                                navigateBackRoute: {
                                    pathname: backPath,
                                    state: {activeTabIndex: 0}
                                }
                            }
                        }), isSmallScreen)}
                        toolbarIconActions={[
                            {
                                icon: <FileDownloadRoundedIcon/>,
                                title: 'Download report',
                                type: 'select',
                                hideIfEmpty: true,
                                menuItems: [
                                    {
                                        label: 'Download PDF',
                                        onClick: downloadPdfReport
                                    },
                                ]
                            }
                        ]}
                        noRowsOverlay={{
                            icon: <ContentPasteOffRoundedIcon/>,
                            text: 'No student form requests yet'
                        }}
                    />
                </>
            }
    ];
    const selfServiceTabs = [
        {
                label: ownFormsLabel,
                testId: 'tab-own-self-service-forms',
                tabSx: FIRST_INNER_SUB_TAB_SX,
                // Maria, 18 October 2026: the same layout as a student's Self-service Forms, and before Student Forms
                component: <SelfServiceForms
                    forms={notificationForms}
                    isLoading={isLoadingNotificationForms || isFetchingNotificationForms}
                    openIndex={selectedNotificationFormIndex}
                    setOpenIndex={setSelectedNotificationFormIndex}
                    navigate={navigate}
                    personId={employeeIdNumber}
                    fetchFn={self ? useLazyGetMyNotificationFormResponsesQuery : useLazyGetEmployeeNotificationFormResponsesQuery}
                    showSubmittedBy={false}
                    bare
                />
            },
        {
                // Maria, 19 October 2026: self-service forms about another member of staff
                label: 'Linked Staff',
                testId: 'tab-linked-staff-self-service-forms',
                tabSx: INNER_SUB_TAB_SX,
                // Maria, 18 October 2026: the same layout as Self-service Forms, with the student each form is about, from
                // the left-hand edge; the member of staff completes every one, so there is no 'Completed By'
                component: <SelfServiceForms
                    forms={linkedSelfServiceForms.filter(form => form.linkedEmployeeId)}
                    isLoading={isLoadingLinkedForms || isFetchingLinkedForms}
                    openIndex={selectedStaffLinkedFormIndex}
                    setOpenIndex={setSelectedStaffLinkedFormIndex}
                    navigate={navigate}
                    personId={employeeIdNumber}
                    fetchFn={self ? useLazyGetMyNotificationFormResponsesQuery : useLazyGetEmployeeNotificationFormResponsesQuery}
                    showSubmittedBy={false}
                    showLinkedTo
                    // Maria, 19 October 2026: under its own tab, ending at the Documents tab
                    bare
                />
            },
        {
                // Maria, 18 October 2026: renamed from 'Linked Self-service Forms'
                label: 'Linked Student',
                testId: 'tab-linked-student-self-service-forms',
                tabSx: INNER_SUB_TAB_SX,
                // Maria, 18 October 2026: the same layout as Self-service Forms, with the student each form is about, from
                // the left-hand edge; the member of staff completes every one, so there is no 'Completed By'
                component: <SelfServiceForms
                    forms={linkedSelfServiceForms.filter(form => form.linkedStudentId)}
                    isLoading={isLoadingLinkedForms || isFetchingLinkedForms}
                    openIndex={selectedLinkedFormIndex}
                    setOpenIndex={setSelectedLinkedFormIndex}
                    navigate={navigate}
                    personId={employeeIdNumber}
                    fetchFn={self ? useLazyGetMyNotificationFormResponsesQuery : useLazyGetEmployeeNotificationFormResponsesQuery}
                    showSubmittedBy={false}
                    showLinkedTo
                    // Maria, 19 October 2026: under its own tab, ending at the Documents tab
                    bare
                />
            }
    ];

    // Maria, 19 October 2026: one outlined box under the open tab, joined to it, from the left-hand edge of that tab to the
    // end of the Documents tab, with the second row of tabs and its list inside it
    const innerTabs = (outerTestId, innerList, index, setIndex) => (
        <UnderTab tabTestId={outerTestId} fill>
            <Box sx={{'& .MuiTabs-root': {backgroundColor: '#e3f5f1', borderBottom: TAB_OUTLINE, pl: 2, minHeight: 0}}}>
            <AdmicityTabs
                tabs={innerList}
                variant={isNarrow ? 'scrollable' : 'standard'}
                tabIndex={index}
                onChange={setIndex}
                hideIndicator
                slots={{panel: {paddingRight: 0, paddingLeft: 0, backgroundColor: '#fff'}}}
            />
            </Box>
        </UnderTab>
    );

    const tabs =
        [
            {
                label: 'Forms',
                tabSx: HOLDS_BUTTONS_SUB_TAB_SX,
                component: innerTabs('tab-forms', formsTabs, formsTabIndex, setFormsTabIndex)
            },
            {
                label: 'Self-service Forms',
                tabSx: HOLDS_BUTTONS_SUB_TAB_SX,
                component: innerTabs('tab-self-service-forms', selfServiceTabs, selfServiceTabIndex, index => {
                    setSelfServiceTabIndex(index);
                    setSelectedNotificationFormIndex(undefined);
                })
            },
            {
                // Maria, 18 October 2026: Communications after Self-service Forms
                label: 'Communications',
                tabSx: STAFF_SUB_TAB_SX,
                // Maria, 18 October 2026: under its tab without a heading row, as on a student's profile, ending in line
                // with the Student Self-service Forms tab
                component: <UnderTab tabTestId="tab-communications" >
                    <CommunicationFormsTable
                        variant="elevation"
                        id={`${key}/communication`}
                        columns={communicationFormsTableColumns}
                        tableProps={{
                            ...staffTableProps,
                            enableSorting: false,
                            enableColumnResizing: false,
                            enableTopToolbar: false,
                            enableFullScreenToggle: false,
                            // Maria, 19 October 2026: a heading row, as on the other lists
                        enableRowActions: true,
                        positionActionsColumn: 'last',
                        displayColumnDefOptions: {'mrt-row-actions': communicationRowButtons()}
                        }}
                        onRowClick={(params) => openCommunicationRow(params, communication => navigate(`${communication.requestId}`), isSmallScreen)}
                        noRowsOverlay={{
                            icon: <ContentPasteOffRoundedIcon/>,
                            text: 'No communication yet'
                        }}
                    />
                </UnderTab>
            },
            {
                label: 'Approvals',
                tabSx: STAFF_SUB_TAB_SX,
                component: <ApprovalFormsTable
                    employeeId={employeeIdNumber}
                    navigate={navigate}
                    inProfile
                />
            },
        ];

    const paperRef = useRef(null);

    // the backend answers 404 while the account has no staff profile, as on My Profile
    if (self && currentEmployeeError?.status === 404) {
        return (
            <Box sx={{pt: 2}}>
                <Alert severity="info" data-testid="my-forms-not-linked">{NOT_LINKED_FORMS_MESSAGE}</Alert>
            </Box>
        );
    }

    return (
        <Paper
            ref={paperRef}
            data-staff-forms-area
            variant="outlined"
            sx={
                isSmallScreen
                    // the toolbar icons are placed on the row of sub-tabs, so the box keeps its position on a small screen
                    ? {position: 'relative', '--tab-outline': STAFF_TAB_OUTLINE, '& .MuiTabs-root': {minHeight: 36}}
                    : {
                        // Maria, 18 October 2026: a white box the height of its table, as on a student's Forms tab
                        position: 'relative',
                        '--tab-outline': STAFF_TAB_OUTLINE,
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 2,
                        borderColor: '#e3e8ef',
                        overflow: 'hidden',
                        // Maria, 19 October 2026: shallower sub-tabs
                        '& .MuiTabs-root': {minHeight: 36}
                    }}
        >
            <AdmicityTabs
                tabs={tabs}
                // Maria, 19 October 2026: on a phone or small tablet the row of sub-tabs scrolls sideways
                variant={isNarrow ? 'scrollable' : 'standard'}
                tabIndex={currentTabIndex}
                onChange={(index) => {
                    setCurrentTabIndex(index);
                    setSelectedNotificationFormIndex(undefined);
                }}
                // Maria, 19 October 2026: no grey line under the tabs; each box draws its own top edge
                hideIndicator
                slots={{
                    panel: {
                        paddingRight: 0,
                        paddingLeft: 0
                    }
                }}
            />
        </Paper>
    );
};

EmployeeForms.propTypes = {
    employeeId: PropTypes.number,
    self: PropTypes.bool
};

export default EmployeeForms;
